import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

// Collapsing right at the top shrinks the page under the finger, which reads
// as a jump; let the reader get past the header first
const COLLAPSE_FROM = 120;
// Smaller moves are jitter: rubber-banding, a scrollbar nudge
const MIN_DELTA = 6;
// While the toolbar animates, the page height shifts under the reader and the
// browser may nudge scrollY; those moves are not scrolling and must not flip
// the state back; longer than the 320 ms transition in the styles
const SETTLE_MS = 400;
// The clip while re-expanding normally ends on transitionend; an engine that
// cannot animate grid tracks (iOS < 16) runs no transition and fires no event,
// hence a fallback
const EXPAND_MS = 380;
const ROW_PROPERTY = 'grid-template-rows';
const OPEN_POPOVER = '.listing-toolbar-collapse [aria-expanded="true"]';

let suppressedUntil = 0;

export const suppressScrollCollapse = () => {
  suppressedUntil = performance.now() + SETTLE_MS;
};

export const useScrollCollapse = () => {
  const collapsed = ref(false);
  // True from the moment the toolbar comes back until its transition ends, so
  // the styles can keep clipping it while it grows
  const expanding = ref(false);
  let lastY = window.scrollY;
  let settleUntil = 0;
  let expandTimer = null;

  const onScroll = () => {
    const y = window.scrollY;
    if (performance.now() < suppressedUntil) {
      lastY = y;
      return;
    }
    // iOS lets scrollY run past the end while rubber-banding; the spring-back
    // would read as scrolling up and bring the toolbar back
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (y >= maxY - MIN_DELTA) {
      lastY = y;
      return;
    }
    const delta = y - lastY;
    if (Math.abs(delta) < MIN_DELTA) return;
    lastY = y;
    if (performance.now() < settleUntil) return;
    if (document.querySelector(OPEN_POPOVER)) return;
    // Never hide the control a keyboard user is on: collapsing would drop the
    // focus into a hidden row. A pointer click leaves focus on the view toggle
    // too, without :focus-visible, and that must not block the collapse.
    const active = document.activeElement;
    if (active?.closest('.listing-toolbar-collapse') && active.matches(':focus-visible')) return;
    const next = delta > 0 && y > COLLAPSE_FROM;
    if (next !== collapsed.value) settleUntil = performance.now() + SETTLE_MS;
    collapsed.value = next;
  };

  const onTransitionEnd = (event) => {
    if (event && event.propertyName !== ROW_PROPERTY) return;
    if (expandTimer) {
      window.clearTimeout(expandTimer);
      expandTimer = null;
    }
    expanding.value = false;
  };

  watch(collapsed, (value, was) => {
    expanding.value = was && !value;
    if (expandTimer) window.clearTimeout(expandTimer);
    expandTimer = expanding.value ? window.setTimeout(() => onTransitionEnd(), EXPAND_MS) : null;
  });

  onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll);
    if (expandTimer) window.clearTimeout(expandTimer);
  });

  return { collapsed, expanding, onTransitionEnd };
};
